/**
 * Admin Login Verified Page
 * 
 * Displayed after an admin clicks the email verification link.
 * Shows success/error message based on verification result.
 */

import React, { useEffect, useState } from 'react';

interface AdminLoginVerifiedPageProps {
  onNavigate?: (page: string) => void;
}

const AdminLoginVerifiedPage: React.FC<AdminLoginVerifiedPageProps> = () => {
  const [success, setSuccess] = useState<boolean | null>(null);
  const [error, setError] = useState<string>('');
  const [email, setEmail] = useState<string>('');

  useEffect(() => {
    // Parse URL parameters
    const params = new URLSearchParams(window.location.search);
    setSuccess(params.get('success') === 'true');
    setError(decodeURIComponent(params.get('error') || ''));
    setEmail(decodeURIComponent(params.get('email') || ''));
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#111827',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        maxWidth: '500px',
        width: '100%',
        backgroundColor: '#1F2937',
        borderRadius: '16px',
        padding: '40px',
        textAlign: 'center',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
        border: '1px solid #D4AF37'
      }}>
        {success === null ? (
          <div style={{ color: '#9CA3AF' }}>Loading...</div>
        ) : success ? (
          <>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>🔐</div>
            <h1 style={{ color: '#D4AF37', fontSize: '28px', marginBottom: '16px' }}>
              Admin Login Verified!
            </h1>
            <p style={{ color: '#D1D5DB', marginBottom: '24px' }}>
              Your admin login has been successfully verified. You can now close this tab and continue in your admin session.
            </p>
            {email && (
              <div style={{
                backgroundColor: '#374151',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '24px'
              }}>
                <div style={{ color: '#9CA3AF', fontSize: '14px', marginBottom: '8px' }}>Verified Admin</div>
                <div style={{ color: '#D4AF37', fontSize: '18px', fontWeight: 'bold' }}>
                  {email}
                </div>
              </div>
            )}
            <div style={{
              backgroundColor: '#065F4620',
              border: '1px solid #10B981',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <p style={{ color: '#6EE7B7', fontSize: '14px', margin: 0 }}>
                ✅ Your admin session is now active. The login modal should automatically close.
              </p>
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>❌</div>
            <h1 style={{ color: '#EF4444', fontSize: '28px', marginBottom: '16px' }}>
              Verification Failed
            </h1>
            <p style={{ color: '#D1D5DB', marginBottom: '24px' }}>
              {error || 'An error occurred during admin login verification.'}
            </p>
            <div style={{
              backgroundColor: '#7F1D1D20',
              border: '1px solid #DC2626',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px'
            }}>
              <p style={{ color: '#FCA5A5', fontSize: '14px', margin: 0 }}>
                If your verification link has expired, please request a new PIN from the admin login page.
              </p>
            </div>
          </>
        )}

        <a
          href="/?admin=true"
          style={{
            display: 'inline-block',
            backgroundColor: '#D4AF37',
            color: '#1a1a2e',
            padding: '12px 24px',
            borderRadius: '8px',
            textDecoration: 'none',
            fontWeight: 'bold',
            marginTop: '16px'
          }}
        >
          Go to Admin Login
        </a>
      </div>
    </div>
  );
};

export default AdminLoginVerifiedPage;

